import { TableHeader, TableRow, TableHead } from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import type { IntuiColumnDef, SortState } from './types'

interface IntuiTableHeaderProps<T> {
  columns: IntuiColumnDef<T>[]
  compact?: boolean
  bordered?: boolean
  stickyHeader?: boolean
  // Sorting
  sortable?: boolean
  sortState: SortState | null
  onSort: (key: string) => void
  // Selection
  selectable?: boolean
  selectionMode?: 'single' | 'multi'
  isAllSelected: boolean
  isIndeterminate: boolean
  onSelectAll: () => void
  // Expand
  expandable?: boolean
  // Actions
  hasRowActions: boolean
}

export function IntuiTableHeader<T>({
  columns,
  compact,
  bordered,
  stickyHeader,
  sortable,
  sortState,
  onSort,
  selectable,
  selectionMode,
  isAllSelected,
  isIndeterminate,
  onSelectAll,
  expandable,
  hasRowActions,
}: IntuiTableHeaderProps<T>) {
  // Figma: header uses px-2 (8px) padding, h-10 (40px) height
  const cellPadding = compact ? 'px-2 py-1' : 'px-2'

  const getSortIcon = (columnKey: string) => {
    if (!sortState || sortState.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
    }
    if (sortState.direction === 'asc') {
      return <ArrowUp className="h-4 w-4 text-foreground" />
    }
    return <ArrowDown className="h-4 w-4 text-foreground" />
  }

  return (
    <TableHeader
      className={cn(stickyHeader && 'sticky top-0 z-10')}
    >
      {/* Figma: no background on header row, just bottom border */}
      <TableRow className="hover:bg-transparent border-b border-border">
        {/* Selection header */}
        {selectable && (
          <TableHead className={cn('w-10', cellPadding)}>
            {selectionMode === 'multi' && (
              <Checkbox
                checked={isIndeterminate ? 'indeterminate' : isAllSelected}
                onCheckedChange={onSelectAll}
                aria-label="Select all rows"
              />
            )}
          </TableHead>
        )}

        {/* Expand header */}
        {expandable && (
          <TableHead className={cn('w-10', cellPadding)} />
        )}

        {/* Column headers */}
        {columns.map((column) => {
          const isSortable =
            column.sortable !== undefined ? column.sortable : sortable
          const isColumnSortable = isSortable && !!column.comparator

          return (
            <TableHead
              key={column.key}
              className={cn(
                cellPadding,
                'h-10 text-foreground font-medium text-sm',
                column.align === 'center' && 'text-center',
                column.align === 'right' && 'text-right',
                isColumnSortable && 'cursor-pointer select-none',
                bordered && 'border-r last:border-r-0',
                column.headerClassName
              )}
              style={{
                width: column.width,
                minWidth: column.minWidth,
              }}
              onClick={isColumnSortable ? () => onSort(column.key) : undefined}
              aria-sort={
                sortState?.key === column.key
                  ? sortState.direction === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : undefined
              }
            >
              <div
                className={cn(
                  'flex items-center gap-1',
                  column.align === 'center' && 'justify-center',
                  column.align === 'right' && 'justify-end'
                )}
              >
                <span>
                  {typeof column.header === 'function'
                    ? column.header()
                    : column.header}
                </span>
                {isColumnSortable && getSortIcon(column.key)}
              </div>
            </TableHead>
          )
        })}

        {/* Actions header */}
        {hasRowActions && (
          <TableHead className={cn('w-12', cellPadding)} />
        )}
      </TableRow>
    </TableHeader>
  )
}
